import type { Locale } from './domain.js';

/**
 * Translation catalogue.
 *
 * French is the source language, not a translation. The business runs in French — every
 * pipeline stage, service name and customer note in the historical data is French — so `fr`
 * is authored first and `en` follows. That ordering matters: writing English first and
 * translating produces stilted French for the people who actually use this all day.
 *
 * Two separate notions of language, never conflated:
 *   - the **user's** locale, which decides the interface, and
 *   - the **customer's** locale, which decides the language of a quote, invoice or SMS
 *     they receive.
 * A francophone customer gets a French invoice even when an anglophone coordinator sends
 * it.
 */

/**
 * The shape of a catalogue, with the literal French strings widened to `string`.
 *
 * `fr` is declared `as const` so its keys are exact, which is what makes a missing key a
 * compile error. Using `typeof fr` directly as the type of `en` would also demand the exact
 * *values* — insisting every English string equal its French counterpart, which is absurd.
 * Widening only the leaves keeps the key checking and drops the value checking.
 */
type Widen<T> = T extends string ? string : { readonly [K in keyof T]: Widen<T[K]> };

export type Catalogue = Widen<typeof fr>;

export const fr = {
  /* Editing a quote or an invoice that has not gone out yet. */
  draft: {
    section: 'Modifier le brouillon',
    edit: 'Modifier les lignes',
    addLine: '+ Ajouter une ligne',
    removeLine: 'Retirer cette ligne',
    lineName: 'Description',
    quantity: 'Qté',
    unitPrice: 'Prix',
    taxable: 'Taxable',
    optional: 'Option',
    preTax: 'Avant taxes :',
    expiresOn: 'Échéance de la soumission',
    dueOn: 'Date d’échéance',
    note: 'Note au client',
    notePlaceholder: 'Visible par le client sur le document.',
    quoteIssued: 'Cette soumission est envoyée. Pour la corriger, créez-en une nouvelle.',
    invoiceIssued: 'Cette facture est émise. Pour la corriger, annulez-la et refacturez.',
    failed: 'Les modifications n’ont pas pu être enregistrées.',
  },

  /*
   * Visits, as scheduled from the job page.
   *
   * `precisionHint` is the whole point of the form: 539 imported bookings are a day plus a
   * period because that is what the customer was told, and the interface must not quietly
   * promise an exact hour nobody gave.
   */
  visit: {
    book: 'Planifier une visite',
    none: 'Aucune visite planifiée',
    move: 'Déplacer',
    cancel: 'Annuler la visite',
    cancelReason: 'Raison de l’annulation',
    cancelPlaceholder: 'Client absent, reporté à sa demande…',
    precision: 'Ce qui a été promis',
    precisionHint: 'Une heure exacte, une plage (« mercredi AM ») ou simplement une journée.',
    precisions: { exact: 'Une heure exacte', period: 'Une plage', day: 'Une journée' },
    periods: { am: 'Matin', pm: 'Après-midi', day: 'Journée', evening: 'Soirée', week: 'Semaine' },
    day: 'Date',
    startTime: 'Heure',
    endTime: 'Fin',
    period: 'Plage',
    assignee: 'Technicien',
    note: 'Note pour la visite',
    notePlaceholder: 'Apporter l’échelle, chien sur place…',
    failed: 'La visite n’a pas pu être enregistrée.',
    status: {
      scheduled: 'Planifiée',
      confirmed: 'Confirmée',
      in_progress: 'En cours',
      completed: 'Terminée',
      cancelled: 'Annulée',
      not_done: 'Non faite',
      no_show: 'Client absent',
    },
  },

  common: {
    all: 'Tout',
    none: 'Aucun',
    status: 'Statut',
    dueDate: 'Échéance',
    copy: 'Copier',
    copied: 'Copié',
    loadMore: 'Charger la suite',
    firstPage: 'Début de la liste',
    nextPage: 'Page suivante',
    clearFilters: 'Effacer les filtres',
    search: 'Rechercher',
    searchPlaceholder: 'Nom, adresse, téléphone, courriel…',
    save: 'Enregistrer',
    cancel: 'Annuler',
    delete: 'Supprimer',
    edit: 'Modifier',
    close: 'Fermer',
    back: 'Retour',
    loading: 'Chargement…',
    noResults: 'Aucun résultat',
    today: "Aujourd'hui",
    yesterday: 'Hier',
    tomorrow: 'Demain',
    retry: 'Réessayer',
    required: 'Obligatoire',
    optional: 'Facultatif',
  },
    /*
     * The document viewer's vocabulary.
     *
     * A viewer is chrome around somebody else's document, so the words have to stay out of the
     * way: what page you are on, and how to get the file. Nothing here describes the document
     * itself — the document does that.
     */
    viewer: {
      page: 'Page {{page}} sur {{count}}',
      previousPage: 'Page précédente',
      nextPage: 'Page suivante',
      zoomIn: 'Agrandir',
      zoomOut: 'Réduire',
      fitWidth: 'Ajuster à la largeur',
      download: 'Télécharger',
      print: 'Imprimer',
      openTab: 'Ouvrir dans un onglet',
      loading: 'Ouverture du document…',
      failed: 'Ce document ne peut pas être affiché.',
      failedBody: 'Le fichier est peut-être endommagé ou dans un format que le navigateur ne lit pas. Vous pouvez toujours le télécharger.',
      noPreview: 'Aperçu impossible pour ce type de fichier',
      of: 'de',
    },

    /*
     * A place, as distinct from the person at it.
     *
     * The vocabulary has to keep "this home" and "the building it is in" apart at every turn,
     * because conflating them turns a useful history into a confident falsehood: the largest
     * building here holds 82 customers.
     */
    /*
     * Judging two transcripts of one recording.
     *
     * "Theirs" and "ours" rather than naming either system: what matters to the reviewer is
     * which account of the call is better, not which vendor produced it.
     */
    transcripts: {
      nav: 'Transcriptions',
      title: 'Comparer les transcriptions',
      lede: 'Écoutez l’appel, puis dites laquelle des deux transcriptions est la meilleure. Espace pour écouter, 1 à 4 pour juger, J et K pour naviguer.',
      theirs: 'Ancienne plateforme',
      ours: 'La nôtre',
      numberRecovered: 'Numéro retrouvé',
      outOf: 'sur {{count}} appels vérifiables',
      judged: 'Jugés',
      noneYet: 'Aucun jugement pour l’instant',
      numberFound: 'Numéro retrouvé',
      numberMissed: 'Numéro manqué',
      nothing: 'Aucune parole détectée',
      onlyDisagreeing: 'Seulement celles qui divergent',
      keys: 'Espace : écouter · 1-4 : juger · J/K : suivant',
      state: { pending: 'À juger', reviewed: 'Jugés', all: 'Tout' },
      verdict: {
        theirs: 'L’ancienne est meilleure',
        ours: 'La nôtre est meilleure',
        equal: 'Équivalentes',
        both_poor: 'Les deux sont mauvaises',
      },
      empty: {
        title: 'Rien à juger',
        body: 'Chaque appel comparable a été évalué. Lancez une nouvelle transcription pour en ajouter.',
      },
    },

    documents: {
      title: 'Fichiers',
      empty: 'Aucun fichier',
      missing: 'Fichier non importé',
      /* Files whose date never made it across. Named rather than hidden or guessed at. */
      undated: 'Date inconnue',
    },

    address: {
      history: 'Historique de l’adresse',
      unit: 'App. {{unit}}',
      thisHome: 'Ce logement',
      wholeBuilding: 'Immeuble ({{count}})',
      occupants: 'Occupants connus',
      customersHere: 'Clients à cette adresse',
      mostRecent: 'Plus récent',
      workHere: 'Travaux à cette adresse',
      noWork: 'Aucun travail enregistré à cette adresse.',
      neverVisited: 'Sans visite planifiée',
      jobCount_one: '{{count}} travail',
      jobCount_other: '{{count}} travaux',
      othersAtPlace_one: '{{count}} autre client a occupé ce logement',
      othersAtPlace_other: '{{count}} autres clients ont occupé ce logement',
      othersInBuilding_one: '{{count}} autre client dans l’immeuble',
      othersInBuilding_other: '{{count}} autres clients dans l’immeuble',
      unitsUnrecorded:
        'Plusieurs clients partagent cette adresse civique sans numéro d’appartement. Il s’agit probablement d’un immeuble dont les logements n’ont jamais été notés — ces personnes n’ont pas nécessairement habité le même logement.',
    },

    /*
     * The audit trail's vocabulary.
     *
     * The log stores machine words — `invoice`, `voided`, `dueOn` — and never a sentence, so
     * one row reads correctly in either language and a revert can act on the same data the
     * reader is looking at. These are the words.
     */
    audit: {
      entity: {
        customer: 'le client',
        job: 'le travail',
        quote: 'la soumission',
        invoice: 'la facture',
        payment: 'le paiement',
        appointment: 'la visite',
        message: 'le message',
        thread: 'la conversation',
        role: 'le rôle',
        team: 'l’accès',
        order: 'le bon de commande',
        notification: 'la notification',
        tenant: 'l’entreprise',
      },
      action: {
        created: 'a créé',
        updated: 'a modifié',
        deleted: 'a supprimé',
        sent: 'a envoyé',
        voided: 'a annulé',
        completed: 'a terminé',
        accepted: 'a accepté',
        declined: 'a refusé',
        stage_changed: 'a déplacé',
        access_changed: 'a changé les accès de',
        resolved: 'a résolu',
        changed: 'a modifié',
      },
      field: {
        status: 'état',
        stageId: 'étape',
        title: 'titre',
        description: 'description',
        note: 'note',
        internalNote: 'note interne',
        dueOn: 'échéance',
        issuedOn: 'émise le',
        startsAt: 'début',
        endsAt: 'fin',
        precision: 'précision',
        period: 'période',
        assigneeId: 'assigné à',
        crewIds: 'équipe',
        services: 'services',
        addressId: 'adresse',
        displayName: 'nom',
        organization: 'entreprise',
        parentId: 'rattaché à',
        total: 'total',
        method: 'mode de paiement',
        transport: 'canal',
        capabilities: 'permissions',
        landing: 'application',
        roleId: 'rôle',
        hourlyRate: 'taux horaire',
        key: 'identifiant',
        name: 'nom',
        jobId: 'travail',
      },
      origin: { portal: 'Portail', field: 'Terrain', importer: 'Import', api: 'API' },
      /** Rendered when a value was empty on one side of the change. */
      empty: '(vide)',
      undo: 'Annuler ce changement',
      undone: 'Changement annulé',
      lede: 'Chaque action est enregistrée : qui, quoi, quand, et depuis quel appareil.',
      actorFilter: 'Personne',
      entityFilter: 'Type',
      dayFilter: 'Jour',
      sort: { at: 'Date', entity: 'Type', action: 'Action', actor: 'Personne' },
      open: 'ouvrir',
      actions: 'actions',
      unavailable: 'Le journal est inaccessible.',
      emptyTitle: 'Aucune action enregistrée',
      emptyBody:
        'Rien ne correspond à ces filtres. Le journal commence au moment où l’enregistrement a été activé — il ne contient pas l’historique antérieur.',
    },
  nav: {
    today: "Aujourd'hui",
    /** The crew board. Named for the question it answers, not for the widget it is. */
    board: 'Sur la route',
    team: 'Équipe',
    journal: 'Journal',
    schedule: 'Horaire',
    jobs: 'Travaux',
    customers: 'Clients',
    quotes: 'Soumissions',
    invoices: 'Factures',
    payments: 'Paiements',
    inbox: 'Messages',
    calls: 'Appels',
    timesheets: 'Feuilles de temps',
    reports: 'Rapports',
    catalogue: 'Catalogue',
    settings: 'Paramètres',
  },
  search: {
    groups: {
      customer: 'Clients',
      job: 'Travaux',
      quote: 'Soumissions',
      invoice: 'Factures',
      call: 'Appels',
      message: 'Messages',
    },
    matchedOn: {
      name: 'nom',
      address: 'adresse',
      phone: 'téléphone',
      email: 'courriel',
      number: 'numéro',
      text: 'texte',
    },
    nearby: 'Près de moi',
    seeAll: 'Voir tous les résultats',
    hint: 'Tapez un nom, une adresse, un numéro de téléphone ou un numéro de facture',
  },
  job: {
    form: {
      new: 'Nouveau travail',
      create: 'Créer le travail',
      lede: 'Le travail est créé à la première étape du tableau ; déplacez-le ensuite d’un clic.',
      findCustomer: 'Trouver le client',
      noCustomer: 'Aucun client trouvé. Créez d’abord la fiche.',
      changeCustomer: 'Changer de client',
      customerGone: 'Ce client n’existe plus.',
      pickAnother: 'Choisir un autre client',
      customerHasNoAddress: 'Ce client n’a aucune adresse. Le travail sera créé sans lieu.',
      noAddress: 'Sans adresse',
      work: 'Le travail',
      title: 'Titre',
      titlePlaceholder: 'Nettoyage de conduits',
      description: 'Demande du client',
      descriptionPlaceholder: 'Dans ses mots : ce qu’il a demandé.',
      descriptionHint: 'Dans ses mots, pas en résumé : c’est ce que le technicien lit.',
      internalNote: 'Note pour l’équipe',
      internalNoteHint: 'Jamais montré au client.',
      source: 'Origine',
      sources: { phone: 'Téléphone', web: 'Site web', walk_in: 'Sans rendez-vous', app: 'Saisie manuelle', api: 'API' },
      saving: 'Création…',
      failed: 'Le travail n’a pas pu être créé. Réessayez.',
    },
    view: { label: 'Affichage', board: 'Tableau', list: 'Liste' },
    filter: {
      searchPlaceholder: 'Nº, client, adresse…',
      stageAll: 'Toutes les étapes',
      stageColumn: 'Colonne',
      assigneeAll: 'Tout le monde',
      serviceAll: 'Tous les services',
      unassignedOnly: 'À assigner',
      clear: 'Effacer les filtres',
    },
    sort: { label: 'Trier', scheduled: 'Date planifiée', created: 'Date de création', balance: 'Solde' },
    board: {
      label: 'Tableau des travaux par étape',
      columnLabel: '{{stage}} — {{count}} travail',
      columnLabel_plural: '{{stage}} — {{count}} travaux',
      empty: 'Aucun travail à cette étape',
      more: 'Voir le {{count}} travail',
      more_plural: 'Voir les {{count}} travaux',
    },
    list: {
      label: 'Liste des travaux',
      count: '{{shown}} de {{total}}',
      next: 'Page suivante',
      stageOf: 'Étape du travail {{number}}',
    },
    tab: {
      work: 'Travail', money: 'Argent', messages: 'Messages',
      time: 'Temps', photos: 'Photos', history: 'Historique',
    },
    sourceKind: { app: 'Application', web: 'Site web', api: 'API', phone: 'Téléphone', import: 'Import' },
    money: { number: 'Numéro', status: 'Statut', issued: 'Émise le', due: 'Échéance', summary: 'Sommaire' },
    message: {
      staff: 'Nous', customer: 'Client', system: 'Système',
      transport: { sms: 'SMS', email: 'Courriel', in_app: 'Application', note: 'Note interne', call: 'Appel' },
    },
    time: { window: 'Période', duration: 'Durée', rate: 'Taux', note: 'Note', approved: 'Approuvé' },
    empty: {
      money: 'Aucune soumission ni facture pour ce travail',
      messages: 'Aucun message',
      time: 'Aucun temps enregistré',
      photos: 'Aucune photo',
      history: 'Aucun historique — l’enregistrement des modifications commence à la mise en service ; les travaux antérieurs n’en ont pas.',
    },
    milestones: 'Jalons',
    scheduled: 'Planifié',
    createdOn: 'Créé le',
    startedAt: 'Début réel',
    completedAt: 'Fin réelle',
    description: 'Description',
    internalNote: 'Note interne',
    appointments: 'Visites planifiées',
    tags: 'Étiquettes',
    source: 'Provenance',
    cost: 'Coût',
    moveStage: "Déplacer l'étape",
    moving: 'Déplacement…',
    tabsLabel: 'Sections du travail',
    customerConversation: 'Ces messages sont la conversation avec le client, pas propres à ce travail.',
    openThread: 'Ouvrir dans Messages',
    one: 'Travail',
    many: 'Travaux',
    number: 'Nº de travail',
    stage: 'Étape',
    unscheduled: 'Non planifié',
    scheduledFor: 'Planifié le {{date}}',
    assignees: 'Assigné à',
    unassigned: 'Non assigné',
    services: 'Services',
    startWork: 'Débuter',
    completeWork: 'Terminer le travail',
    onSite: 'Sur place',
    balanceDue: 'Solde dû',
    margin: 'Marge',
  },
  customer: {
    edit: {
      title: 'Modifier la fiche',
      lede: 'L’identité se sauvegarde avec le bouton. Les téléphones, courriels et adresses s’appliquent immédiatement.',
      gone: 'Cette fiche est introuvable.',
      revert: 'Annuler les modifications',
      done: 'Terminé',
      add: 'Ajouter',
      primary: 'principal',
      /* Offered, jamais appliqué : accepter écarte ce qui est saisi ici. */
      superseded: 'Quelqu’un a modifié cette fiche pendant que vous l’éditiez.',
      reload: 'Charger sa version',
      contactNow: 'Téléphones et courriels — appliqué immédiatement',
      addressesNow: 'Adresses — appliqué immédiatement',
      addAddress: '+ Ajouter une adresse',
      statusActive: 'Actif',
      statusInactive: 'Inactif',
      statusArchived: 'Archivé',
      statusHint: 'Archiver conserve tout l’historique ; rien n’est supprimé.',
      addressKinds: { service: 'Service', billing: 'Facturation', shipping: 'Livraison', main: 'Principale' },
      phoneFailed: 'Le téléphone n’a pas pu être modifié.',
      emailFailed: 'Le courriel n’a pas pu être modifié.',
      addressFailed: 'L’adresse n’a pas pu être modifiée.',
    },
    /*
     * The creation form.
     *
     * `nameRule` exists instead of marking three fields required: none of them is, one of them
     * has to be, and the only honest way to say that is a sentence.
     */
    form: {
      lede: 'Les coordonnées saisies ici sont enregistrées avec la fiche, en une seule opération.',
      identity: 'Identité',
      firstName: 'Prénom',
      lastName: 'Nom',
      organization: 'Organisation',
      nameRule: 'Au moins un des trois : prénom, nom ou organisation.',
      locale: 'Langue du client',
      localeFr: 'Français',
      localeEn: 'Anglais',
      localeHint: 'La langue de ses factures et de ses messages, pas la vôtre.',
      billing: 'Facturation',
      taxable: 'Taxes applicables',
      contact: 'Coordonnées',
      phoneKind: 'Type de téléphone',
      emailKind: 'Type de courriel',
      phonePlaceholder: '514 966-2020',
      emailPlaceholder: 'client@exemple.ca',
      addPhone: '+ Ajouter un téléphone',
      addEmail: '+ Ajouter un courriel',
      removePhone: 'Retirer ce téléphone',
      removeEmail: 'Retirer ce courriel',
      kind: {
        mobile: 'Cellulaire',
        home: 'Résidence',
        work: 'Travail',
        main: 'Principal',
        other: 'Autre',
        personal: 'Personnel',
        professional: 'Professionnel',
        billing: 'Facturation',
      },
      serviceAddress: 'Adresse de service',
      line1: 'Adresse',
      line1Placeholder: '207-300 Rue Sherbrooke',
      line1Hint: 'Inscrivez le numéro d’unité sur cette ligne : 207-300 Rue Sherbrooke.',
      line2: 'Complément',
      postalCode: 'Code postal',
      addressKind: 'Type d’adresse',
      accessNote: 'Accès',
      accessNotePlaceholder: 'Code de portail, où stationner, sonner deux fois…',
      accessNoteHint: 'Ce que le technicien lit avant de cogner.',
      noteHint: 'Visible par l’équipe seulement. Jamais envoyé au client.',
      saving: 'Enregistrement…',
      failed: 'La fiche n’a pas pu être créée. Réessayez.',
    },
    name: 'Nom',
    city: 'Ville',
    tags: 'Étiquettes',
    lastActivity: 'Dernière activité',
    new: 'Nouveau client',
    emptyTitle: "Aucun client pour l'instant",
    emptyBody: 'Ajoutez votre premier client pour planifier un travail.',
    emptyFilteredBody: 'Aucun client ne correspond à ces filtres. Modifiez-les ou créez la fiche.',
    overduePortion: 'dont {{amount}} en retard',
    credit: '{{amount}} en crédit au compte',
    internalNote: 'Note interne',
    noSmsConsent: 'Pas de consentement SMS',
    bounced: 'Courriel invalide',
    openWork: 'Travaux en cours',
    /*
     * Two empty states, and the difference is not pedantry.
     *
     * `noOpenWork` answers "is anything in progress" — right on the jobs screen, where that is
     * the question. On a customer's own file it was wrong: a customer we have served six times
     * and finished every job for read "Aucun travail en cours", which sounds like we have never
     * worked for them. The file lists all their work now, so it needs the flatter statement.
     */
    noOpenWork: 'Aucun travail en cours',
    noWork: 'Aucun travail',
    noHistory: 'Rien d’enregistré pour ce client.',
    showAll: 'Tout afficher ({{count}})',
    showLess: 'Réduire',
    viewThread: 'Voir la conversation',
    one: 'Client',
    many: 'Clients',
    phone: 'Téléphone',
    email: 'Courriel',
    address: 'Adresse',
    serviceAddress: 'Adresse de service',
    billingAddress: 'Adresse de facturation',
    accessNote: "Note d'accès",
    history: 'Historique',
    balance: 'Solde',
    billToParent: 'Facturer au compte parent',
    language: 'Langue de correspondance',
  },
  quote: {
    one: 'Soumission',
    many: 'Soumissions',
    status: {
      draft: 'Brouillon',
      pending: 'En attente',
      accepted: 'Acceptée',
      declined: 'Refusée',
      expired: 'Expirée',
    },
    optionalLine: 'Ligne optionnelle',
    acceptedOn: 'Acceptée le {{date}}',
    send: 'Envoyer la soumission',
    searchPlaceholder: 'Nº de soumission, client…',
    noNumber: 'sans numéro',
    preview: 'Aperçu de la soumission',
    /** Only pending, unexpired quotes: adding settled history would ruin it as a forecast. */
    openValue: '{{amount}} en attente de réponse',
    emptyFiltered: 'Aucune soumission ne correspond à ces filtres.',
  },
  invoice: {
    one: 'Facture',
    many: 'Factures',
    status: {
      draft: 'Brouillon',
      awaiting_payment: 'En attente de paiement',
      partial: 'Partiellement payée',
      paid: 'Payée',
      voided: 'Annulée',
    },
    dueOn: 'Échéance le {{date}}',
    overdue: 'En retard de {{count}} jour',
    overdue_plural: 'En retard de {{count}} jours',
    subtotal: 'Sous-total',
    taxes: 'Taxes',
    total: 'Total',
    paid: 'Payé',
    balance: 'Solde',
    neverOpened: 'Jamais ouverte par le client',
    searchPlaceholder: 'Nº de facture, client…',
    noNumber: 'sans numéro',
    preview: 'Aperçu de la facture',
    overdueOnly: 'En retard seulement',
    receivable: '{{amount}} à recevoir',
    /*
     * Kept apart from the receivable figure on purpose: a draft was never sent, so it is
     * unbilled work rather than money owed. Adding the two together overstates both.
     */
    unbilledAmount: '{{amount}} non facturé',
    unbilledHint: 'Brouillons jamais envoyés — travail non facturé',
    emptyFiltered: 'Aucune facture ne correspond à ces filtres.',
  },
  payment: {
    one: 'Paiement',
    many: 'Paiements',
    collect: 'Encaisser un paiement',
    method: {
      credit_card: 'Carte de crédit',
      e_transfer: 'Virement Interac',
      check: 'Chèque',
      cash: 'Comptant',
      interac: 'Interac',
      ach: 'Prélèvement bancaire',
      other: 'Autre',
    },
    tip: 'Pourboire',
    requiresConnection: 'Le paiement par carte exige une connexion Internet.',
  },
  time: {
    clockIn: 'Débuter mon temps',
    clockOut: 'Arrêter mon temps',
    running: 'En cours depuis {{duration}}',
    hoursLogged: '{{hours}} h enregistrées',
    myWeek: 'Ma semaine',
  },
  calls: {
    summary: 'Résumé',
    summaryPending: 'Résumé en préparation',
    inbound: 'Entrant',
    outbound: 'Sortant',
    missed: 'Manqué',
    recording: 'Enregistrement',
    transcript: 'Transcription',
    dial: 'Composer',
    hangUp: 'Raccrocher',
    mute: 'Sourdine',
    hold: 'En attente',
    simulated: 'Téléphonie simulée — aucun appel réel n’est émis.',
  },
  /*
   * One phone call, described.
   *
   * The database stores machine words — `voicemail`, `invoicing`, `veryNegative` — and never a
   * French sentence, for the same reason the audit log does: one row then reads correctly in
   * either language, and a filter can act on the same value the reader is looking at.
   *
   * Two of these carry a distinction that must survive translation:
   *
   *   - `outcome.unknown` is a **state**, not a fault. Roughly a quarter of the imported calls
   *     were never transcribed, so nothing in the record says whether anybody picked up.
   *     "Indéterminé" says that; "Erreur" or "—" would say the system is broken.
   *   - `reportedBy` labels what a caller recited into a voicemail — a name, an address, an
   *     invoice number. That is a **claim**, not a verified fact, and the wording has to keep
   *     saying so wherever it is rendered, because somebody will otherwise dispatch a van to an
   *     address nobody checked.
   */
  call: {
    outcome: {
      answered: 'Répondu',
      voicemail: 'Message vocal',
      missed: 'Manqué',
      unknown: 'Indéterminé',
    },
    direction: {
      inbound: 'Entrant',
      outbound: 'Sortant',
    },
    reason: {
      sales: 'Vente',
      support: 'Service',
      information: 'Information',
      invoicing: 'Facturation',
      personal: 'Personnel',
    },
    sentiment: {
      veryPositive: 'Très satisfait',
      positive: 'Satisfait',
      neutral: 'Neutre',
      negative: 'Insatisfait',
      veryNegative: 'Très insatisfait',
    },
    reportedBy: 'Selon l’appelant',
    lowConfidence: 'Extraction peu fiable',
  },
  offline: {
    online: 'En ligne',
    offline: 'Hors ligne',
    syncing: 'Synchronisation ({{count}})',
    pendingWork: 'Travail en attente d’envoi',
    savedOnDevice: 'Enregistré sur l’appareil',
    savedToOffice: 'Envoyé au bureau',
    lastSynced: 'Dernière synchronisation : {{time}}',
    willSendWhenOnline: 'Sera envoyé dès le retour du réseau.',
    needsAttention: 'Nécessite votre attention',
    storageRefused:
      'Le navigateur pourrait effacer les données non envoyées. Installez l’application pour éviter toute perte.',
  },
  auth: {
    signIn: 'Connexion',
    emailPrompt: 'Entrez votre adresse courriel',
    sendCode: 'Recevoir un code',
    sending: 'Un instant…',
    codeSent: 'Un code a été envoyé à {{email}}',
    codePrompt: 'Entrez le code à 6 chiffres',
    verifying: 'Vérification…',
    resend: 'Renvoyer le code',
    /*
     * Deliberately vague about *which* of the two it was: saying "expired" confirms to somebody
     * guessing that the code they tried was once real. Written as a full sentence rather than a
     * label because it is read by a person who has just failed to get in and needs to be told
     * what to do next, not what happened.
     */
    invalidCode: 'Ce code ne correspond pas, ou il a expiré. Demandez-en un nouveau.',
    transportFailed: 'La connexion au serveur a échoué. Réessayez dans un instant.',
    switchBusiness: 'Changer d’entreprise',
  },
  message: {
    transport: {
      sms: 'SMS',
      email: 'Courriel',
      in_app: 'Application',
      note: 'Note interne',
      call: 'Appel',
    },
    staff: 'Nous',
    customer: 'Client',
    system: 'Système',
  },
  /*
   * The intake queue: calls with nowhere to land.
   *
   * Voicemails, and calls from numbers that match no customer. Two words carry weight here:
   *
   *   - `resolution.converted` means **a customer record now exists** for the person who rang.
   *     It is a filing outcome, not a sale — "Client créé", never "Converti", which would read
   *     as a pipeline win and would be counted as one by somebody eventually.
   *   - `empty.body` is shown when the queue is clear, which is the good state. It says the work
   *     is done and where the next one will appear, rather than reporting an absence of data.
   */
  intake: {
    title: 'Demandes entrantes',
    lede: 'Les appels qui attendent une réponse : messages vocaux et appels d’inconnus.',
    waiting: '{{count}} en attente',
    unmatched: 'Numéro inconnu',
    unmatchedBody: 'Ce numéro ne correspond à aucun client.',
    empty: {
      title: 'Rien en attente',
      body: 'Chaque appel a été traité. Les nouveaux messages vocaux apparaîtront ici.',
    },
    state: { open: 'À traiter', done: 'Traité' },
    resolution: {
      called_back: 'Rappelé',
      /** A customer record now exists for the caller — a filing outcome, never a sale. */
      converted: 'Client créé',
      not_needed: 'Aucune suite',
      duplicate: 'Doublon',
    },
    action: {
      markDone: 'Marquer traité',
      reopen: 'Rouvrir',
      linkCustomer: 'Associer à un client',
      createCustomer: 'Créer le client',
      listen: 'Écouter',
    },
    note: 'Note',
    notePlaceholder: 'Ce qui a été fait…',
    transcript: 'Transcription',
    summary: 'Résumé',
    handledBy: 'Traité par {{name}}',
  },
  /*
   * Work done and never charged for.
   *
   * The vocabulary insists on one distinction throughout: a draft is **not** a receivable. The
   * customer has not been asked for the money, so nothing here may be worded as a debt — which
   * is exactly the confusion that let $36 694 of unbilled work be reported as money on its way in.
   */
  unbilled: {
    title: 'À facturer',
    lede: 'Travail réalisé dont la facture est restée en brouillon. Personne n’a encore été invité à payer.',
    totalLabel: 'Total non facturé',
    draftCount: '{{count}} brouillon',
    draftCount_plural: '{{count}} brouillons',
    readyLabel: 'Prêt à envoyer',
    readyNote: '{{count}} sur des travaux terminés',
    filterOn: 'filtre actif',
    oldestLabel: 'Le plus ancien',
    days: '{{count}} j',
    medianNote: 'médiane {{count}} j',
    caption: 'Brouillons, du plus ancien au plus récent',
    invoiceNumber: 'Facture {{number}}',
    draftNoNumber: 'Brouillon sans numéro',
    emptyTitle: 'Rien en attente de facturation',
    emptyReady: 'Aucun brouillon sur un travail terminé. Les autres brouillons attendent la fin du travail.',
    emptyAll: 'Tout le travail réalisé a été facturé.',
    footnote:
      'Un brouillon n’est pas une créance : le client n’a rien reçu. Ces montants sont volontairement exclus du total « à recevoir » de l’écran',
  },
  errors: {
    generic: 'Une erreur est survenue.',
    notFound: 'Introuvable',
    forbidden: 'Vous n’avez pas accès à cette information.',
    offlineAction: 'Cette action exige une connexion.',
    conflict: 'Le bureau a modifié cette fiche pendant que vous étiez hors ligne.',
  },
  /*
   * The vocabulary of a list: filtering it, ordering it, paging it, taking it away as a file.
   *
   * One block rather than a copy per screen, because the toolbar is one component. The moment
   * "Période" is worded differently on factures and on soumissions, the two screens stop
   * reading as the same control and people stop trusting that it does the same thing.
   */
  list: {
    filters: 'Filtres',
    search: 'Rechercher',
    /* --- period ------------------------------------------------------------------- */
    period: 'Période',
    periodAll: 'Tout l’historique',
    periodCustom: 'Dates précises',
    window: {
      '7d': '7 derniers jours',
      '30d': '30 derniers jours',
      '90d': '90 derniers jours',
      '365d': '12 derniers mois',
      all: 'Tout l’historique',
    },
    from: 'Du',
    to: 'Au',
    apply: 'Appliquer',
    /** Pins the period being looked at as the one every list opens on. */
    makeDefault: 'Ouvrir mes listes sur cette période',
    defaultSaved: 'Période par défaut enregistrée',
    dateField: 'Filtrer sur',
    /** Which date the period applies to. The same 30 days means different rows on each. */
    field: {
      issued: 'Date d’émission',
      due: 'Échéance',
      paid: 'Date de paiement',
      expires: 'Date d’expiration',
      created: 'Date de création',
      scheduled: 'Date planifiée',
      activity: 'Dernière activité',
    },
    /* --- active filters ----------------------------------------------------------- */
    activeFilters: 'Filtres actifs',
    remove: 'Retirer : {{label}}',
    clearAll: 'Tout effacer',
    /* --- sorting ------------------------------------------------------------------ */
    sortBy: 'Trier par {{column}}',
    sortedAscending: 'trié du plus petit au plus grand',
    sortedDescending: 'trié du plus grand au plus petit',
    /* --- export ------------------------------------------------------------------- */
    export: 'Exporter',
    exportCsv: 'Exporter en CSV',
    exporting: 'Préparation du fichier…',
    exportEmpty: 'Rien à exporter avec ces filtres.',
    exportFailed: 'L’exportation a échoué. Réessayez.',
    /** Said before the download, so nobody discovers the cap by counting rows afterwards. */
    exportCapped: '{{total}} lignes correspondent. Le fichier s’arrêtera à {{limit}} — resserrez la période pour tout obtenir.',
    exportTruncated: 'Fichier tronqué à {{limit}} lignes sur {{total}}.',
    exportDone: '{{count}} ligne exportée',
    exportDone_plural: '{{count}} lignes exportées',
    /* --- the window notice -------------------------------------------------------- */
    windowNotice: 'La période masque {{count}} élément plus ancien.',
    windowNotice_plural: 'La période masque {{count}} éléments plus anciens.',
    windowEmpty: 'Aucun résultat dans cette période.',
    showEverything: 'Voir tout l’historique',
    /* --- pager -------------------------------------------------------------------- */
    perPage: 'Par page',
    resultsPerPage: 'Résultats par page',
    pagination: 'Pagination — {{label}}',
    previousPage: 'Page précédente',
    nextPage: 'Page suivante',
    pageNumber: 'Page {{page}}',
    rangeOf: 'de',
    yes: 'oui',
    no: 'non',
    /*
     * Column headings, shared by the tables and by the first row of every export.
     *
     * The same words in both places on purpose: somebody who exports what they were looking at
     * must be able to find the column they were reading.
     */
    column: {
      number: 'Numéro',
      customer: 'Client',
      organization: 'Entreprise',
      status: 'Statut',
      issuedOn: 'Émise le',
      dueOn: 'Échéance',
      paidAt: 'Payée le',
      expiresOn: 'Expire le',
      acceptedAt: 'Acceptée le',
      declinedAt: 'Refusée le',
      sentAt: 'Envoyée le',
      firstViewedAt: 'Ouverte le',
      createdAt: 'Créée le',
      subtotal: 'Sous-total',
      taxTotal: 'Taxes',
      total: 'Total',
      paidTotal: 'Payé',
      balance: 'Solde',
      cost: 'Coût',
      invoiced: 'Facturé',
      job: 'Travail',
      appointment: 'Visite',
      name: 'Nom',
      locale: 'Langue',
      phone: 'Téléphone',
      email: 'Courriel',
      address: 'Adresse',
      city: 'Ville',
      region: 'Province',
      postalCode: 'Code postal',
      tags: 'Étiquettes',
      openBalance: 'Solde ouvert',
      overdueBalance: 'En retard',
      lastActivityAt: 'Dernière activité',
      title: 'Titre',
      stage: 'Étape',
      scheduledStart: 'Début planifié',
      scheduledEnd: 'Fin planifiée',
      assignees: 'Assigné à',
      services: 'Services',
      laborMinutes: 'Minutes travaillées',
      ageDays: 'Âge (jours)',
      at: 'Horodatage',
      actor: 'Personne',
      entity: 'Type',
      entityId: 'Identifiant',
      action: 'Action',
      origin: 'Provenance',
      changes: 'Changements',
      subject: 'Sujet',
      messageCount: 'Messages',
      lastMessageAt: 'Dernier message',
      channel: 'Canal',
      awaitingReply: 'En attente de réponse',
      preview: 'Aperçu',
      /** The unlabelled last column of a table, so its header is not silent to a reader. */
      actions: 'Actions',
    },
  },
} as const;

export const en: Catalogue = {
  draft: {
    section: 'Edit draft',
    edit: 'Edit lines',
    addLine: '+ Add a line',
    removeLine: 'Remove this line',
    lineName: 'Description',
    quantity: 'Qty',
    unitPrice: 'Price',
    taxable: 'Taxable',
    optional: 'Optional',
    preTax: 'Before tax:',
    expiresOn: 'Quote expires',
    dueOn: 'Due date',
    note: 'Note to the customer',
    notePlaceholder: 'Visible to the customer on the document.',
    quoteIssued: 'This quote has been sent. To correct it, raise a new one.',
    invoiceIssued: 'This invoice is issued. To correct it, void it and re-invoice.',
    failed: 'The changes could not be saved.',
  },

  visit: {
    book: 'Schedule a visit',
    none: 'No visit scheduled',
    move: 'Move',
    cancel: 'Cancel visit',
    cancelReason: 'Reason for cancelling',
    cancelPlaceholder: 'Customer away, postponed at their request…',
    precision: 'What was promised',
    precisionHint: 'An exact time, a range ("Wednesday AM") or simply a day.',
    precisions: { exact: 'An exact time', period: 'A range', day: 'A day' },
    periods: { am: 'Morning', pm: 'Afternoon', day: 'All day', evening: 'Evening', week: 'That week' },
    day: 'Date',
    startTime: 'Time',
    endTime: 'Ends',
    period: 'Range',
    assignee: 'Technician',
    note: 'Note for the visit',
    notePlaceholder: 'Bring the ladder, dog on site…',
    failed: 'The visit could not be saved.',
    status: {
      scheduled: 'Scheduled',
      confirmed: 'Confirmed',
      in_progress: 'In progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
      not_done: 'Not done',
      no_show: 'No show',
    },
  },

  common: {
    all: 'All',
    none: 'None',
    status: 'Status',
    dueDate: 'Due',
    copy: 'Copy',
    copied: 'Copied',
    loadMore: 'Load more',
    firstPage: 'Start of list',
    nextPage: 'Next page',
    clearFilters: 'Clear filters',
    search: 'Search',
    searchPlaceholder: 'Name, address, phone, email…',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    close: 'Close',
    back: 'Back',
    loading: 'Loading…',
    noResults: 'No results',
    today: 'Today',
    yesterday: 'Yesterday',
    tomorrow: 'Tomorrow',
    retry: 'Retry',
    required: 'Required',
    optional: 'Optional',
  },
    viewer: {
      page: 'Page {{page}} of {{count}}',
      previousPage: 'Previous page',
      nextPage: 'Next page',
      zoomIn: 'Zoom in',
      zoomOut: 'Zoom out',
      fitWidth: 'Fit to width',
      download: 'Download',
      print: 'Print',
      openTab: 'Open in a tab',
      loading: 'Opening the document…',
      failed: 'This document cannot be displayed.',
      failedBody: 'The file may be damaged, or in a format the browser cannot read. You can still download it.',
      noPreview: 'No preview for this kind of file',
      of: 'of',
    },

    transcripts: {
      nav: 'Transcripts',
      title: 'Compare transcripts',
      lede: 'Listen to the call, then say which transcript is better. Space to play, 1–4 to judge, J and K to move.',
      theirs: 'Previous platform',
      ours: 'Ours',
      numberRecovered: 'Number recovered',
      outOf: 'of {{count}} checkable calls',
      judged: 'Judged',
      noneYet: 'No judgements yet',
      numberFound: 'Number recovered',
      numberMissed: 'Number missed',
      nothing: 'No speech detected',
      onlyDisagreeing: 'Only where they differ',
      keys: 'Space: play · 1-4: judge · J/K: next',
      state: { pending: 'To judge', reviewed: 'Judged', all: 'All' },
      verdict: {
        theirs: 'The old one is better',
        ours: 'Ours is better',
        equal: 'Equivalent',
        both_poor: 'Both are poor',
      },
      empty: {
        title: 'Nothing to judge',
        body: 'Every comparable call has been judged. Run another transcription pass to add more.',
      },
    },

    documents: {
      title: 'Files',
      empty: 'No files',
      missing: 'File not imported',
      undated: 'Date unknown',
    },

    address: {
      history: 'Address history',
      unit: 'Unit {{unit}}',
      thisHome: 'This home',
      wholeBuilding: 'Building ({{count}})',
      occupants: 'Known occupants',
      customersHere: 'Customers at this address',
      mostRecent: 'Most recent',
      workHere: 'Work at this address',
      noWork: 'No work recorded at this address.',
      neverVisited: 'No visit scheduled',
      jobCount_one: '{{count}} job',
      jobCount_other: '{{count}} jobs',
      othersAtPlace_one: '{{count}} other customer has been at this home',
      othersAtPlace_other: '{{count}} other customers have been at this home',
      othersInBuilding_one: '{{count}} other customer in the building',
      othersInBuilding_other: '{{count}} other customers in the building',
      unitsUnrecorded:
        'Several customers share this street address with no unit number. This is most likely a building whose units were never recorded — these people have not necessarily lived in the same home.',
    },

    audit: {
      entity: {
        customer: 'the customer',
        job: 'the job',
        quote: 'the quote',
        invoice: 'the invoice',
        payment: 'the payment',
        appointment: 'the visit',
        message: 'the message',
        thread: 'the conversation',
        role: 'the role',
        team: 'access for',
        order: 'the order',
        notification: 'the notification',
        tenant: 'the business',
      },
      action: {
        created: 'created',
        updated: 'updated',
        deleted: 'deleted',
        sent: 'sent',
        voided: 'voided',
        completed: 'completed',
        accepted: 'accepted',
        declined: 'declined',
        stage_changed: 'moved',
        access_changed: 'changed access for',
        resolved: 'resolved',
        changed: 'changed',
      },
      field: {
        status: 'status',
        stageId: 'stage',
        title: 'title',
        description: 'description',
        note: 'note',
        internalNote: 'internal note',
        dueOn: 'due date',
        issuedOn: 'issued on',
        startsAt: 'start',
        endsAt: 'end',
        precision: 'precision',
        period: 'period',
        assigneeId: 'assigned to',
        crewIds: 'crew',
        services: 'services',
        addressId: 'address',
        displayName: 'name',
        organization: 'organisation',
        parentId: 'parent',
        total: 'total',
        method: 'payment method',
        transport: 'channel',
        capabilities: 'permissions',
        landing: 'application',
        roleId: 'role',
        hourlyRate: 'hourly rate',
        key: 'key',
        name: 'name',
        jobId: 'job',
      },
      origin: { portal: 'Portal', field: 'Field', importer: 'Import', api: 'API' },
      empty: '(empty)',
      undo: 'Undo this change',
      undone: 'Change undone',
      lede: 'Every action is recorded: who, what, when, and from which device.',
      actorFilter: 'Person',
      entityFilter: 'Type',
      dayFilter: 'Day',
      sort: { at: 'Date', entity: 'Type', action: 'Action', actor: 'Person' },
      open: 'open',
      actions: 'actions',
      unavailable: 'The activity log is unavailable.',
      emptyTitle: 'No activity recorded',
      emptyBody:
        'Nothing matches these filters. The log starts from the moment recording was switched on — it does not hold anything from before that.',
    },
  nav: {
    today: 'Today',
    board: 'On the road',
    team: 'Team',
    journal: 'Activity',
    schedule: 'Schedule',
    jobs: 'Jobs',
    customers: 'Customers',
    quotes: 'Quotes',
    invoices: 'Invoices',
    payments: 'Payments',
    inbox: 'Inbox',
    calls: 'Calls',
    timesheets: 'Timesheets',
    reports: 'Reports',
    catalogue: 'Catalogue',
    settings: 'Settings',
  },
  search: {
    groups: {
      customer: 'Customers',
      job: 'Jobs',
      quote: 'Quotes',
      invoice: 'Invoices',
      call: 'Calls',
      message: 'Messages',
    },
    matchedOn: { name: 'name', address: 'address', phone: 'phone', email: 'email', number: 'number', text: 'text' },
    nearby: 'Near me',
    seeAll: 'See all results',
    hint: 'Type a name, address, phone number or invoice number',
  },
  job: {
    form: {
      new: 'New job',
      create: 'Create job',
      lede: 'The job starts in the first column of the board; move it from there in one click.',
      findCustomer: 'Find the customer',
      noCustomer: 'No customer found. Create the record first.',
      changeCustomer: 'Change customer',
      customerGone: 'That customer no longer exists.',
      pickAnother: 'Pick another customer',
      customerHasNoAddress: 'This customer has no address. The job will be created without a place.',
      noAddress: 'No address',
      work: 'The work',
      title: 'Title',
      titlePlaceholder: 'Duct cleaning',
      description: "Customer's request",
      descriptionPlaceholder: 'In their words: what they asked for.',
      descriptionHint: 'In their words, not summarised: this is what the technician reads.',
      internalNote: 'Note for the team',
      internalNoteHint: 'Never shown to the customer.',
      source: 'Source',
      sources: { phone: 'Phone', web: 'Website', walk_in: 'Walk-in', app: 'Entered manually', api: 'API' },
      saving: 'Creating…',
      failed: 'The job could not be created. Try again.',
    },
    view: { label: 'View', board: 'Board', list: 'List' },
    filter: {
      searchPlaceholder: 'No., customer, address…',
      stageAll: 'All stages',
      stageColumn: 'Column',
      assigneeAll: 'Everyone',
      serviceAll: 'All services',
      unassignedOnly: 'Needs assigning',
      clear: 'Clear filters',
    },
    sort: { label: 'Sort', scheduled: 'Scheduled date', created: 'Created date', balance: 'Balance' },
    board: {
      label: 'Jobs by stage',
      columnLabel: '{{stage}} — {{count}} job',
      columnLabel_plural: '{{stage}} — {{count}} jobs',
      empty: 'No jobs at this stage',
      more: 'See {{count}} job',
      more_plural: 'See {{count}} jobs',
    },
    list: {
      label: 'Job list',
      count: '{{shown}} of {{total}}',
      next: 'Next page',
      stageOf: 'Stage of job {{number}}',
    },
    tab: {
      work: 'Work', money: 'Money', messages: 'Messages',
      time: 'Time', photos: 'Photos', history: 'History',
    },
    sourceKind: { app: 'App', web: 'Website', api: 'API', phone: 'Phone', import: 'Import' },
    money: { number: 'Number', status: 'Status', issued: 'Issued', due: 'Due', summary: 'Summary' },
    message: {
      staff: 'Us', customer: 'Customer', system: 'System',
      transport: { sms: 'SMS', email: 'Email', in_app: 'In app', note: 'Internal note', call: 'Call' },
    },
    time: { window: 'Window', duration: 'Duration', rate: 'Rate', note: 'Note', approved: 'Approved' },
    empty: {
      money: 'No quotes or invoices for this job',
      messages: 'No messages',
      time: 'No time logged',
      photos: 'No photos',
      history: 'No history — changes have only been recorded since this system went live, so earlier work has none.',
    },
    milestones: 'Milestones',
    scheduled: 'Scheduled',
    createdOn: 'Created',
    startedAt: 'Actually started',
    completedAt: 'Actually finished',
    description: 'Description',
    internalNote: 'Internal note',
    appointments: 'Scheduled visits',
    tags: 'Tags',
    source: 'Source',
    cost: 'Cost',
    moveStage: 'Move stage',
    moving: 'Moving…',
    tabsLabel: 'Job sections',
    customerConversation: 'These messages are the conversation with the customer, not specific to this job.',
    openThread: 'Open in Inbox',
    one: 'Job',
    many: 'Jobs',
    number: 'Job no.',
    stage: 'Stage',
    unscheduled: 'Unscheduled',
    scheduledFor: 'Scheduled for {{date}}',
    assignees: 'Assigned to',
    unassigned: 'Unassigned',
    services: 'Services',
    startWork: 'Start',
    completeWork: 'Complete job',
    onSite: 'On site',
    balanceDue: 'Balance due',
    margin: 'Margin',
  },
  customer: {
    edit: {
      title: 'Edit customer',
      lede: 'Identity saves with the button. Phones, emails and addresses apply immediately.',
      gone: 'This record cannot be found.',
      revert: 'Discard changes',
      done: 'Done',
      add: 'Add',
      primary: 'primary',
      superseded: 'Somebody changed this record while you were editing it.',
      reload: 'Load their version',
      contactNow: 'Phones and emails — applied immediately',
      addressesNow: 'Addresses — applied immediately',
      addAddress: '+ Add an address',
      statusActive: 'Active',
      statusInactive: 'Inactive',
      statusArchived: 'Archived',
      statusHint: 'Archiving keeps the whole history; nothing is deleted.',
      addressKinds: { service: 'Service', billing: 'Billing', shipping: 'Shipping', main: 'Main' },
      phoneFailed: 'The phone could not be changed.',
      emailFailed: 'The email could not be changed.',
      addressFailed: 'The address could not be changed.',
    },
    form: {
      lede: 'Contact details entered here are saved with the record, in one operation.',
      identity: 'Identity',
      firstName: 'First name',
      lastName: 'Last name',
      organization: 'Organisation',
      nameRule: 'At least one of the three: first name, last name or organisation.',
      locale: "Customer's language",
      localeFr: 'French',
      localeEn: 'English',
      localeHint: 'The language of their invoices and messages, not yours.',
      billing: 'Billing',
      taxable: 'Taxes apply',
      contact: 'Contact details',
      phoneKind: 'Phone type',
      emailKind: 'Email type',
      phonePlaceholder: '514 966-2020',
      emailPlaceholder: 'customer@example.ca',
      addPhone: '+ Add a phone',
      addEmail: '+ Add an email',
      removePhone: 'Remove this phone',
      removeEmail: 'Remove this email',
      kind: {
        mobile: 'Mobile',
        home: 'Home',
        work: 'Work',
        main: 'Main',
        other: 'Other',
        personal: 'Personal',
        professional: 'Professional',
        billing: 'Billing',
      },
      serviceAddress: 'Service address',
      line1: 'Address',
      line1Placeholder: '207-300 Rue Sherbrooke',
      line1Hint: 'Put the unit number on this line: 207-300 Rue Sherbrooke.',
      line2: 'Additional line',
      postalCode: 'Postal code',
      addressKind: 'Address type',
      accessNote: 'Access',
      accessNotePlaceholder: 'Gate code, where to park, ring twice…',
      accessNoteHint: 'What the technician reads before knocking.',
      noteHint: 'Visible to the team only. Never sent to the customer.',
      saving: 'Saving…',
      failed: 'The record could not be created. Try again.',
    },
    name: 'Name',
    city: 'City',
    tags: 'Tags',
    lastActivity: 'Last activity',
    new: 'New customer',
    emptyTitle: 'No customers yet',
    emptyBody: 'Add your first customer to schedule work.',
    emptyFilteredBody: 'No customer matches these filters. Adjust them, or create the record.',
    overduePortion: 'including {{amount}} overdue',
    credit: '{{amount}} credit on account',
    internalNote: 'Internal note',
    noSmsConsent: 'No SMS consent',
    bounced: 'Email undeliverable',
    openWork: 'Open work',
    noOpenWork: 'No open work',
    noWork: 'No work recorded',
    noHistory: 'Nothing recorded for this customer yet.',
    showAll: 'Show all ({{count}})',
    showLess: 'Show less',
    viewThread: 'View conversation',
    one: 'Customer',
    many: 'Customers',
    phone: 'Phone',
    email: 'Email',
    address: 'Address',
    serviceAddress: 'Service address',
    billingAddress: 'Billing address',
    accessNote: 'Access note',
    history: 'History',
    balance: 'Balance',
    billToParent: 'Bill to parent account',
    language: 'Correspondence language',
  },
  quote: {
    one: 'Quote',
    many: 'Quotes',
    status: { draft: 'Draft', pending: 'Pending', accepted: 'Accepted', declined: 'Declined', expired: 'Expired' },
    optionalLine: 'Optional line',
    acceptedOn: 'Accepted on {{date}}',
    send: 'Send quote',
    searchPlaceholder: 'Quote no., customer…',
    noNumber: 'no number',
    preview: 'Quote preview',
    openValue: '{{amount}} awaiting an answer',
    emptyFiltered: 'No quote matches these filters.',
  },
  invoice: {
    one: 'Invoice',
    many: 'Invoices',
    status: {
      draft: 'Draft',
      awaiting_payment: 'Awaiting payment',
      partial: 'Partially paid',
      paid: 'Paid',
      voided: 'Voided',
    },
    dueOn: 'Due {{date}}',
    overdue: '{{count}} day overdue',
    overdue_plural: '{{count}} days overdue',
    subtotal: 'Subtotal',
    taxes: 'Taxes',
    total: 'Total',
    paid: 'Paid',
    balance: 'Balance',
    neverOpened: 'Never opened by the customer',
    searchPlaceholder: 'Invoice no., customer…',
    noNumber: 'no number',
    preview: 'Invoice preview',
    overdueOnly: 'Overdue only',
    receivable: '{{amount}} receivable',
    unbilledAmount: '{{amount}} unbilled',
    unbilledHint: 'Drafts never sent — work not yet charged for',
    emptyFiltered: 'No invoice matches these filters.',
  },
  payment: {
    one: 'Payment',
    many: 'Payments',
    collect: 'Collect payment',
    method: {
      credit_card: 'Credit card',
      e_transfer: 'Interac e-Transfer',
      check: 'Cheque',
      cash: 'Cash',
      interac: 'Interac',
      ach: 'Bank debit',
      other: 'Other',
    },
    tip: 'Tip',
    requiresConnection: 'Card payments require an internet connection.',
  },
  time: {
    clockIn: 'Start my time',
    clockOut: 'Stop my time',
    running: 'Running for {{duration}}',
    hoursLogged: '{{hours}} h logged',
    myWeek: 'My week',
  },
  calls: {
    summary: 'Summary',
    summaryPending: 'Summary in progress',
    inbound: 'Inbound',
    outbound: 'Outbound',
    missed: 'Missed',
    recording: 'Recording',
    transcript: 'Transcript',
    dial: 'Dial',
    hangUp: 'Hang up',
    mute: 'Mute',
    hold: 'Hold',
    simulated: 'Simulated telephony — no real call is placed.',
  },
  call: {
    outcome: {
      answered: 'Answered',
      voicemail: 'Voicemail',
      missed: 'Missed',
      /** A state, not an error: a quarter of the imported calls were never transcribed. */
      unknown: 'Not known',
    },
    direction: {
      inbound: 'Incoming',
      outbound: 'Outgoing',
    },
    reason: {
      sales: 'Sales',
      support: 'Support',
      information: 'Information',
      invoicing: 'Billing',
      personal: 'Personal',
    },
    sentiment: {
      veryPositive: 'Very positive',
      positive: 'Positive',
      neutral: 'Neutral',
      negative: 'Negative',
      veryNegative: 'Very negative',
    },
    /** A claim the caller made, never a verified record. */
    reportedBy: 'As stated by the caller',
    lowConfidence: 'Low-confidence extraction',
  },
  offline: {
    online: 'Online',
    offline: 'Offline',
    syncing: 'Syncing ({{count}})',
    pendingWork: 'Work waiting to send',
    savedOnDevice: 'Saved on this device',
    savedToOffice: 'Sent to the office',
    lastSynced: 'Last synced: {{time}}',
    willSendWhenOnline: 'Will send as soon as you have signal.',
    needsAttention: 'Needs your attention',
    storageRefused: 'The browser may clear unsent work. Install the app to avoid losing anything.',
  },
  auth: {
    signIn: 'Sign in',
    emailPrompt: 'Enter your email address',
    sendCode: 'Send me a code',
    sending: 'One moment…',
    codeSent: 'A code was sent to {{email}}',
    codePrompt: 'Enter the 6-digit code',
    verifying: 'Checking…',
    resend: 'Resend code',
    invalidCode: 'That code does not match, or it has expired. Ask for a new one.',
    transportFailed: 'Could not reach the server. Try again in a moment.',
    switchBusiness: 'Switch business',
  },
  message: {
    transport: {
      sms: 'SMS',
      email: 'Email',
      in_app: 'In app',
      note: 'Internal note',
      call: 'Call',
    },
    staff: 'Us',
    customer: 'Customer',
    system: 'System',
  },
  intake: {
    title: 'Incoming requests',
    lede: 'Calls waiting on someone: voicemails, and calls from people we don’t know.',
    waiting: '{{count}} waiting',
    unmatched: 'Unknown number',
    unmatchedBody: 'This number matches no customer.',
    empty: {
      title: 'Nothing waiting',
      body: 'Every call has been dealt with. New voicemails will appear here.',
    },
    state: { open: 'To handle', done: 'Handled' },
    resolution: {
      called_back: 'Called back',
      /** A customer record now exists for the caller — a filing outcome, never a sale. */
      converted: 'Became a customer',
      not_needed: 'No action needed',
      duplicate: 'Duplicate',
    },
    action: {
      markDone: 'Mark handled',
      reopen: 'Reopen',
      linkCustomer: 'Link to a customer',
      createCustomer: 'Create the customer',
      listen: 'Listen',
    },
    note: 'Note',
    notePlaceholder: 'What was done…',
    transcript: 'Transcript',
    summary: 'Summary',
    handledBy: 'Handled by {{name}}',
  },
  unbilled: {
    title: 'To be billed',
    lede: 'Work carried out whose invoice is still a draft. Nobody has been asked to pay yet.',
    totalLabel: 'Total unbilled',
    draftCount: '{{count}} draft',
    draftCount_plural: '{{count}} drafts',
    readyLabel: 'Ready to send',
    readyNote: '{{count}} on finished jobs',
    filterOn: 'filter on',
    oldestLabel: 'Oldest',
    days: '{{count}} d',
    medianNote: 'median {{count}} d',
    caption: 'Drafts, oldest first',
    invoiceNumber: 'Invoice {{number}}',
    draftNoNumber: 'Draft with no number',
    emptyTitle: 'Nothing waiting to be billed',
    emptyReady: 'No draft on a finished job. The others are waiting for the work to end.',
    emptyAll: 'Everything carried out has been billed.',
    footnote:
      'A draft is not a receivable: the customer has received nothing. These amounts are deliberately left out of the "receivable" total on',
  },
  errors: {
    generic: 'Something went wrong.',
    notFound: 'Not found',
    forbidden: 'You do not have access to this information.',
    offlineAction: 'This action requires a connection.',
    conflict: 'The office changed this record while you were offline.',
  },
  list: {
    filters: 'Filters',
    search: 'Search',
    period: 'Period',
    periodAll: 'All history',
    periodCustom: 'Specific dates',
    window: {
      '7d': 'Last 7 days',
      '30d': 'Last 30 days',
      '90d': 'Last 90 days',
      '365d': 'Last 12 months',
      all: 'All history',
    },
    from: 'From',
    to: 'To',
    apply: 'Apply',
    makeDefault: 'Open my lists on this period',
    defaultSaved: 'Default period saved',
    dateField: 'Filter on',
    field: {
      issued: 'Issue date',
      due: 'Due date',
      paid: 'Payment date',
      expires: 'Expiry date',
      created: 'Created date',
      scheduled: 'Scheduled date',
      activity: 'Last activity',
    },
    activeFilters: 'Active filters',
    remove: 'Remove: {{label}}',
    clearAll: 'Clear all',
    sortBy: 'Sort by {{column}}',
    sortedAscending: 'sorted smallest first',
    sortedDescending: 'sorted largest first',
    export: 'Export',
    exportCsv: 'Export to CSV',
    exporting: 'Preparing the file…',
    exportEmpty: 'Nothing to export with these filters.',
    exportFailed: 'The export failed. Try again.',
    exportCapped: '{{total}} rows match. The file will stop at {{limit}} — narrow the period to get them all.',
    exportTruncated: 'File truncated to {{limit}} rows out of {{total}}.',
    exportDone: '{{count}} row exported',
    exportDone_plural: '{{count}} rows exported',
    windowNotice: 'The period is hiding {{count}} older item.',
    windowNotice_plural: 'The period is hiding {{count}} older items.',
    windowEmpty: 'Nothing in this period.',
    showEverything: 'Show all history',
    perPage: 'Per page',
    resultsPerPage: 'Results per page',
    pagination: 'Pagination — {{label}}',
    previousPage: 'Previous page',
    nextPage: 'Next page',
    pageNumber: 'Page {{page}}',
    rangeOf: 'of',
    yes: 'yes',
    no: 'no',
    column: {
      number: 'Number',
      customer: 'Customer',
      organization: 'Organisation',
      status: 'Status',
      issuedOn: 'Issued',
      dueOn: 'Due',
      paidAt: 'Paid on',
      expiresOn: 'Expires',
      acceptedAt: 'Accepted on',
      declinedAt: 'Declined on',
      sentAt: 'Sent on',
      firstViewedAt: 'Opened on',
      createdAt: 'Created',
      subtotal: 'Subtotal',
      taxTotal: 'Taxes',
      total: 'Total',
      paidTotal: 'Paid',
      balance: 'Balance',
      cost: 'Cost',
      invoiced: 'Invoiced',
      job: 'Job',
      appointment: 'Visit',
      name: 'Name',
      locale: 'Language',
      phone: 'Phone',
      email: 'Email',
      address: 'Address',
      city: 'City',
      region: 'Province',
      postalCode: 'Postal code',
      tags: 'Tags',
      openBalance: 'Open balance',
      overdueBalance: 'Overdue',
      lastActivityAt: 'Last activity',
      title: 'Title',
      stage: 'Stage',
      scheduledStart: 'Scheduled start',
      scheduledEnd: 'Scheduled end',
      assignees: 'Assigned to',
      services: 'Services',
      laborMinutes: 'Minutes worked',
      ageDays: 'Age (days)',
      at: 'Timestamp',
      actor: 'Person',
      entity: 'Type',
      entityId: 'Identifier',
      action: 'Action',
      origin: 'Source',
      changes: 'Changes',
      subject: 'Subject',
      messageCount: 'Messages',
      lastMessageAt: 'Last message',
      channel: 'Channel',
      awaitingReply: 'Awaiting reply',
      preview: 'Preview',
      actions: 'Actions',
    },
  },
};

export const catalogues: Record<Locale, Catalogue> = { fr, en };

/**
 * Pick a localised value out of a `Translatable` column (pipeline stage names, service
 * names), falling back to French because French is guaranteed present.
 */
export function localized(value: Record<string, string> | null | undefined, locale: Locale): string {
  if (!value) return '';
  return value[locale] || value.fr || Object.values(value)[0] || '';
}
